export const AFFILIATE_RECRUITMENT_STEPS = [
  "welcome",
  "audience",
  "channel",
  "demo",
  "plan",
] as const;

export type AffiliateRecruitmentStep = (typeof AFFILIATE_RECRUITMENT_STEPS)[number];

export const AFFILIATE_RECRUITMENT_AUDIENCES = [
  {
    id: "language_learners",
    label: "Language learners",
    description: "People who understand more than they can comfortably say.",
    campaignLead: "Knowing the words is different from using them in conversation.",
  },
  {
    id: "students_between_lessons",
    label: "Students between lessons",
    description: "Learners who need a low-pressure place to keep speaking.",
    campaignLead: "The days between lessons are a good time to practise speaking.",
  },
  {
    id: "travel_global",
    label: "Travel and global communities",
    description: "People preparing for a trip, move, course, or new connection.",
    campaignLead: "Practise the conversation before you need it in real life.",
  },
  {
    id: "ai_education",
    label: "AI and education audiences",
    description: "People looking for useful learning tools they can see in action.",
    campaignLead: "Here is what an AI speaking-practice session feels like.",
  },
] as const;

export type AffiliateRecruitmentAudienceId = (typeof AFFILIATE_RECRUITMENT_AUDIENCES)[number]["id"];

export const AFFILIATE_RECRUITMENT_CHANNELS = [
  {
    id: "video_social",
    label: "Video or social",
    description: "Short videos, reviews, posts, or creator channels.",
    planTitle: "Show one speaking moment",
    planAction:
      "Record your reaction to a real prompt, explain who the practice suits, and add your tracked link.",
  },
  {
    id: "newsletter_blog",
    label: "Newsletter or blog",
    description: "Articles, resource lists, email, or longer reviews.",
    planTitle: "Write around the speaking problem",
    planAction:
      "Open with the moment a learner freezes, show the product demo, and explain who ChickyTutor may help.",
  },
  {
    id: "class_community",
    label: "Class or community",
    description: "Learner groups, courses, coaching, or private communities.",
    planTitle: "Recommend a useful next practice",
    planAction:
      "Share the demo with a clear reason it may help your group, then invite questions instead of repeating the link.",
  },
  {
    id: "paid_ads",
    label: "Paid ads",
    description: "Search, social, or performance campaigns.",
    planTitle: "Lead with an accurate use case",
    planAction:
      "Use current product claims, disclose the affiliate relationship, and avoid ChickyTutor brand-keyword bidding.",
  },
] as const;

export type AffiliateRecruitmentChannelId = (typeof AFFILIATE_RECRUITMENT_CHANNELS)[number]["id"];

export const DEFAULT_AFFILIATE_DISCLOSURE =
  "I may earn a commission if you subscribe through this link, at no extra cost to you.";

export type AffiliateStarterPlan = {
  audienceLabel: string;
  channelLabel: string;
  title: string;
  campaignLead: string;
  action: string;
  disclosure: string;
};

export function isAffiliateRecruitmentAudienceId(
  value: unknown
): value is AffiliateRecruitmentAudienceId {
  return AFFILIATE_RECRUITMENT_AUDIENCES.some((audience) => audience.id === value);
}

export function isAffiliateRecruitmentChannelId(
  value: unknown
): value is AffiliateRecruitmentChannelId {
  return AFFILIATE_RECRUITMENT_CHANNELS.some((channel) => channel.id === value);
}

export function getAffiliateStarterPlan(
  audienceId: AffiliateRecruitmentAudienceId,
  channelId: AffiliateRecruitmentChannelId
): AffiliateStarterPlan {
  const audience = AFFILIATE_RECRUITMENT_AUDIENCES.find((candidate) => candidate.id === audienceId);
  const channel = AFFILIATE_RECRUITMENT_CHANNELS.find((candidate) => candidate.id === channelId);

  if (!audience || !channel) {
    throw new Error("Unknown affiliate onboarding choice");
  }

  return {
    audienceLabel: audience.label,
    channelLabel: channel.label,
    title: channel.planTitle,
    campaignLead: audience.campaignLead,
    action: channel.planAction,
    disclosure: DEFAULT_AFFILIATE_DISCLOSURE,
  };
}
